// M9 – Import đơn từ MySpa (6.9): Tải file → Ghép cột → Kiểm tra → Import + Lịch sử / Hoàn tác
import { useEffect, useMemo, useState } from 'react';
import { Modal, Progress, Radio, Select, Steps, Table, Tabs, Upload, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import * as XLSX from 'xlsx';
import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, RotateCcw, XCircle } from 'lucide-react';
import { BRANCHES, CATALOG, METHOD_LABEL, type Customer, type ImportBatch, type Order, type PaymentMethod, type PricedLine } from './data';
import usePosStore from './store';
import { VN_MOBILE_REGEX, exportExcel, fmtDateTime, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';

type FieldKey = 'code' | 'date' | 'branch' | 'phone' | 'customer' | 'itemCode' | 'itemName' | 'qty' | 'price' | 'discount' | 'paid' | 'method' | 'staff';
const FIELDS: { key: FieldKey; label: string; required: boolean; aliases: string[]; example: string | number }[] = [
  { key: 'code', label: 'Mã đơn MySpa', required: true, aliases: ['ma don myspa', 'ma don', 'ma hoa don', 'so hoa don'], example: 'HD000123' },
  { key: 'date', label: 'Ngày', required: true, aliases: ['ngay', 'ngay tao', 'thoi gian', 'ngay ban'], example: '15/08/2026 14:30' },
  { key: 'branch', label: 'Chi nhánh', required: true, aliases: ['chi nhanh', 'co so'], example: 'Khothi Quận 1' },
  { key: 'phone', label: 'SĐT khách', required: false, aliases: ['sdt khach', 'sdt', 'so dien thoai', 'dien thoai'], example: '0901234567' },
  { key: 'customer', label: 'Tên khách', required: false, aliases: ['ten khach', 'khach hang', 'ho ten'], example: 'Nguyễn Bích Ngọc' },
  { key: 'itemCode', label: 'Mã dịch vụ', required: false, aliases: ['ma dich vu', 'ma dv', 'ma hang', 'ma sp'], example: 'DV-THERMAGE' },
  { key: 'itemName', label: 'Tên dịch vụ', required: true, aliases: ['ten dich vu', 'dich vu', 'ten hang', 'san pham'], example: 'Trẻ hóa da Thermage FLX' },
  { key: 'qty', label: 'Số lượng', required: true, aliases: ['so luong', 'sl'], example: 1 },
  { key: 'price', label: 'Đơn giá', required: true, aliases: ['don gia', 'gia'], example: 45000000 },
  { key: 'discount', label: 'Giảm giá', required: false, aliases: ['giam gia', 'chiet khau', 'ck'], example: 4500000 },
  { key: 'paid', label: 'Đã thanh toán', required: false, aliases: ['da thanh toan', 'da thu', 'thanh toan'], example: 40500000 },
  { key: 'method', label: 'Phương thức', required: false, aliases: ['phuong thuc', 'hinh thuc thanh toan', 'pttt'], example: 'Chuyển khoản' },
  { key: 'staff', label: 'Nhân viên', required: false, aliases: ['nhan vien', 'nv', 'ktv', 'tu van'], example: 'Lan' },
];
const MAPPING_KEY = 'pos-import-mapping';
const MAX_ROWS = 20000;
const MAX_SIZE = 10 * 1024 * 1024;

const norm = (s: unknown) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const parseDate = (v: unknown): Date | null => {
  if (v instanceof Date && !isNaN(v.getTime())) return v;
  const m = String(v ?? '').trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2}))?$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4] || 0), Number(m[5] || 0));
  return d.getDate() === Number(m[1]) && d.getMonth() === Number(m[2]) - 1 ? d : null;
};
const parseNum = (v: unknown): number | null => {
  if (v === '' || v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[.\s,đ]/g, ''));
  return Number.isFinite(n) ? n : NaN;
};
const matchBranch = (v: unknown) => {
  const n = norm(v);
  if (!n) return null;
  return BRANCHES.find(b => n === norm(b.name) || n === b.short.toLowerCase() || n === norm(b.name.replace('Khothi ', '')) || n.endsWith(`quan ${b.short.slice(1)}`)) || null;
};
const METHOD_BY_NAME: Record<string, PaymentMethod> = Object.fromEntries(Object.entries(METHOD_LABEL).map(([k, v]) => [norm(v), k as PaymentMethod]));

interface CheckedRow {
  index: number;
  raw: Record<string, unknown>;
  code: string;
  level: 'ok' | 'warning' | 'error';
  messages: string[];
  date: Date | null;
  branchId: string | null;
  phone: string;
  customerName: string;
  itemId: string | null;
  itemName: string;
  qty: number;
  price: number;
  discount: number;
  paid: number;
  method: PaymentMethod | null;
  staff: string;
}

export default function PosImport() {
  const { can, userName } = usePosAuth();
  const [tab, setTab] = useState('import');
  return (
    <PosShell title="Import đơn từ MySpa" subtitle="Đơn import không trừ kho, không sinh giao dịch thu, không tính lại khuyến mãi (BR9.4).">
      {!can('import') ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-800">Bạn chưa có quyền pos.import.</div>
      ) : (
        <Tabs activeKey={tab} onChange={setTab} items={[
          { key: 'import', label: 'Import', children: <ImportWizard userName={userName} onDone={() => setTab('history')} /> },
          { key: 'history', label: 'Lịch sử import', children: <History /> },
        ]} />
      )}
    </PosShell>
  );
}

function ImportWizard({ userName, onDone }: { userName: string; onDone: () => void }) {
  const orders = usePosStore(s => s.orders);
  const customers = usePosStore(s => s.customers);
  const addCustomer = usePosStore(s => s.addCustomer);
  const importOrders = usePosStore(s => s.importOrders);

  const [step, setStep] = useState(0);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [mapping, setMapping] = useState<Partial<Record<FieldKey, string>>>({});
  const [levelFilter, setLevelFilter] = useState<'all' | 'ok' | 'warning' | 'error'>('all');
  const [mode, setMode] = useState<'skip' | 'overwrite'>('skip');
  const [progress, setProgress] = useState(-1);
  const [result, setResult] = useState<ImportBatch | null>(null);

  // Cảnh báo khi đóng trang lúc đang import
  useEffect(() => {
    if (progress < 0 || progress >= 100) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [progress]);

  const downloadTemplate = () => exportExcel('Mau_import_MySpa.xlsx', 'Đơn hàng', [
    Object.fromEntries(FIELDS.map(f => [f.label, f.example])),
    Object.fromEntries(FIELDS.map(f => [f.label, f.key === 'itemCode' ? 'SP-SERUMB5' : f.key === 'itemName' ? 'Serum B5' : f.key === 'qty' ? 2 : f.key === 'price' ? 450000 : f.key === 'discount' ? 0 : f.key === 'paid' ? 900000 : f.example])),
    Object.fromEntries(FIELDS.map(f => [f.label, f.key === 'code' ? 'HD000124' : f.key === 'date' ? '16/08/2026 10:05' : f.key === 'branch' ? 'Khothi Quận 3' : f.key === 'phone' ? '0912345678' : f.key === 'customer' ? 'Trần Thị Mai' : f.key === 'itemCode' ? 'DV-HIFU' : f.key === 'itemName' ? 'Nâng cơ Hifu' : f.key === 'qty' ? 1 : f.key === 'price' ? 15000000 : f.key === 'discount' ? 0 : f.key === 'paid' ? 10000000 : f.key === 'method' ? 'Tiền mặt' : 'Minh'])),
  ]);

  const readFile = (file: File) => {
    if (file.size > MAX_SIZE) { message.error('File tối đa 10MB'); return false; }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        // CSV đọc nguyên dạng chữ để không mất số 0 đầu SĐT / mã
        const wb = XLSX.read(e.target?.result, { type: 'array', cellDates: true, raw: /\.csv$/i.test(file.name) });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '', raw: true });
        if (!data.length) return message.error('File không có dữ liệu');
        if (data.length > MAX_ROWS) return message.error(`File tối đa ${MAX_ROWS.toLocaleString('vi-VN')} dòng`);
        const hs = Object.keys(data[0]);
        // Tự ghép cột theo tên không dấu; ưu tiên cấu hình đã lưu lần trước
        let saved: Partial<Record<FieldKey, string>> = {};
        try { saved = JSON.parse(localStorage.getItem(MAPPING_KEY) || '{}'); } catch { /* bỏ qua */ }
        const auto: Partial<Record<FieldKey, string>> = {};
        FIELDS.forEach(f => {
          if (saved[f.key] && hs.includes(saved[f.key]!)) auto[f.key] = saved[f.key];
          else auto[f.key] = hs.find(h => norm(h) === norm(f.label) || f.aliases.includes(norm(h)));
        });
        setFileName(file.name);
        setHeaders(hs);
        setRows(data);
        setMapping(auto);
        setResult(null);
        setProgress(-1);
        setStep(1);
      } catch {
        message.error('Không đọc được file. Kiểm tra định dạng .xlsx / .xls / .csv');
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const missingRequired = FIELDS.filter(f => f.required && !mapping[f.key]);

  const checked: CheckedRow[] = useMemo(() => {
    if (step < 2) return [];
    const val = (r: Record<string, unknown>, k: FieldKey) => (mapping[k] ? r[mapping[k]!] : '');
    const existing = new Set(orders.map(o => o.id));
    return rows.map((r, index) => {
      const errors: string[] = [];
      const warnings: string[] = [];
      const code = String(val(r, 'code') ?? '').trim();
      const itemName = String(val(r, 'itemName') ?? '').trim();
      if (!code) errors.push('Thiếu mã đơn');
      if (!itemName) errors.push('Thiếu tên dịch vụ');
      const rawDate = val(r, 'date');
      const date = parseDate(rawDate);
      if (rawDate === '' || rawDate === undefined) errors.push('Thiếu ngày'); else if (!date) errors.push('Ngày không hợp lệ (dd/MM/yyyy HH:mm)');
      const qty = parseNum(val(r, 'qty'));
      const price = parseNum(val(r, 'price'));
      const discount = parseNum(val(r, 'discount'));
      const paid = parseNum(val(r, 'paid'));
      if (qty === null) errors.push('Thiếu số lượng'); else if (isNaN(qty) || qty <= 0) errors.push('Số lượng phải là số > 0');
      if (price === null) errors.push('Thiếu đơn giá'); else if (isNaN(price) || price < 0) errors.push('Đơn giá phải là số ≥ 0');
      if (discount !== null && (isNaN(discount) || discount < 0)) errors.push('Giảm giá phải là số ≥ 0');
      if (paid !== null && (isNaN(paid) || paid < 0)) errors.push('Đã thanh toán phải là số ≥ 0');
      const branch = matchBranch(val(r, 'branch'));
      if (!branch) errors.push('Chi nhánh không tồn tại');
      let phone = String(val(r, 'phone') ?? '').replace(/\D/g, '');
      // Ô SĐT kiểu số trong Excel làm mất số 0 đầu
      if (/^[35789]\d{8}$/.test(phone)) phone = `0${phone}`;
      if (phone && !VN_MOBILE_REGEX.test(phone)) warnings.push('SĐT không hợp lệ – tạo đơn không gắn khách');
      else if (phone && !customers.some(c => c.phone === phone)) warnings.push('Không tìm thấy khách – sẽ tạo khách mới (Từ MySpa)');
      const itemCode = String(val(r, 'itemCode') ?? '').trim().toUpperCase();
      const item = itemCode ? CATALOG.find(c => c.code === itemCode) : undefined;
      if (!item) warnings.push('Không tìm thấy dịch vụ theo mã – giữ tên gốc');
      if (code && existing.has(`MS-${code}`)) warnings.push(`Đơn đã import trước đó – sẽ ${mode === 'skip' ? 'bỏ qua' : 'ghi đè'}`);
      const methodName = norm(val(r, 'method'));
      return {
        index, raw: r, code, date, branchId: branch?.id || null, phone: VN_MOBILE_REGEX.test(phone) ? phone : '',
        customerName: String(val(r, 'customer') ?? '').trim(), itemId: item?.id || null, itemName, qty: Number(qty) || 0, price: Number(price) || 0,
        discount: Number(discount) || 0, paid: Number(paid) || 0, method: METHOD_BY_NAME[methodName] || null, staff: String(val(r, 'staff') ?? '').trim(),
        level: errors.length ? 'error' : warnings.length ? 'warning' : 'ok', messages: [...errors, ...warnings],
      };
    });
  }, [step, rows, mapping, orders, customers, mode]);

  const valid = checked.filter(r => r.level !== 'error');
  const orderCodes = Array.from(new Set(valid.map(r => r.code)));
  const existingIds = new Set(orders.map(o => o.id));
  const willSkip = mode === 'skip' ? orderCodes.filter(c => existingIds.has(`MS-${c}`)).length : 0;
  const stats = {
    total: checked.length, orders: new Set(checked.map(r => r.code).filter(Boolean)).size,
    ok: checked.filter(r => r.level === 'ok').length, warning: checked.filter(r => r.level === 'warning').length, error: checked.filter(r => r.level === 'error').length,
  };

  const runImport = async () => {
    const codes = orderCodes.filter(c => mode === 'overwrite' || !existingIds.has(`MS-${c}`));
    setProgress(0);
    const built: Order[] = [];
    const created = new Map<string, Customer>();
    const BATCH = 500;
    for (let i = 0; i < codes.length; i += BATCH) {
      codes.slice(i, i + BATCH).forEach(code => {
        const group = valid.filter(r => r.code === code);
        const first = group[0];
        let customer = first.phone ? customers.find(c => c.phone === first.phone) || created.get(first.phone) : undefined;
        if (!customer && first.phone) {
          customer = addCustomer({ name: first.customerName || `Khách ${first.phone}`, phone: first.phone, source: 'MySpa' });
          created.set(first.phone, customer);
        }
        const lines: PricedLine[] = group.map((r, j) => {
          const gross = r.price * r.qty;
          const disc = Math.min(r.discount, gross);
          const item = r.itemId ? CATALOG.find(c => c.id === r.itemId) : undefined;
          return {
            key: `ms-${code}-${j}`, itemId: r.itemId || '', code: item?.code || '', name: r.itemName, type: item?.type || 'service', listPrice: r.price, qty: r.qty,
            discountType: 'đ', discountValue: disc, staffId: '', note: r.staff ? `NV MySpa: ${r.staff}` : '',
            gross, promoAmount: 0, lineDiscountAmount: disc, afterLine: gross - disc, allocated: 0, net: gross - disc, refundedQty: 0, refundedAmount: 0,
          };
        });
        const subtotal = lines.reduce((s, l) => s + l.gross, 0);
        const lineDiscount = lines.reduce((s, l) => s + l.lineDiscountAmount, 0);
        const total = subtotal - lineDiscount;
        const paidRaw = group.reduce((s, r) => s + r.paid, 0);
        const paid = Math.min(total, mapping.paid ? paidRaw : total);
        const at = first.date!.toISOString();
        built.push({
          id: `MS-${code}`, branchId: first.branchId!, createdAt: at, cashier: first.staff || 'MySpa',
          customerId: customer?.id || null, customerName: customer?.name || first.customerName || 'Khách lẻ', customerPhone: customer?.phone || '',
          lines, totals: { subtotal, itemPromo: 0, lineDiscount, orderPromo: 0, orderDiscount: 0, voucher: 0, rounding: 0, total },
          orderDiscountInput: { type: '%', value: 0 }, voucherCode: '', note: first.method ? `Thanh toán MySpa: ${METHOD_LABEL[first.method]}` : '',
          paid, refunded: 0, debt: total - paid, status: total - paid > 0 ? 'debt' : 'completed', source: 'myspa', printCount: 0,
          logs: [{ at: new Date().toISOString(), by: userName, action: `Import từ MySpa (${fileName})` }],
        });
      });
      setProgress(Math.round(((i + BATCH) / Math.max(codes.length, 1)) * 100));
      await new Promise(r => setTimeout(r, 250));
    }
    const batch = importOrders({
      fileName, at: new Date().toISOString(), by: userName, totalRows: stats.total, orders: built.length, success: built.length,
      skipped: willSkip, failed: stats.error, mode,
    }, built);
    localStorage.setItem(MAPPING_KEY, JSON.stringify(mapping));
    setProgress(100);
    setResult(batch);
  };

  const downloadErrors = () => exportExcel(`Loi_import_${fileName.replace(/\.[^.]+$/, '')}.xlsx`, 'Lỗi',
    checked.filter(r => r.level !== 'ok').map(r => ({ 'Dòng': r.index + 2, ...Object.fromEntries(Object.entries(r.raw).map(([k, v]) => [k, v instanceof Date ? fmtDateTime(v.toISOString()) : (v as string)])), 'Mức': r.level === 'error' ? 'Lỗi' : 'Cảnh báo', 'Lỗi': r.messages.join('; ') })));

  const previewColumns: ColumnsType<CheckedRow> = [
    { title: 'Dòng', width: 60, render: (_, r) => r.index + 2 },
    { title: 'Mức', width: 90, render: (_, r) => r.level === 'error' ? <span className="font-semibold text-red-600">Lỗi</span> : r.level === 'warning' ? <span className="font-semibold text-amber-600">Cảnh báo</span> : <span className="text-green-700">Hợp lệ</span> },
    { title: 'Mã đơn', width: 110, dataIndex: 'code' },
    { title: 'Ngày', width: 140, render: (_, r) => (r.date ? fmtDateTime(r.date.toISOString()) : '—') },
    { title: 'Chi nhánh', width: 90, render: (_, r) => BRANCHES.find(b => b.id === r.branchId)?.short || '—' },
    { title: 'Khách', width: 160, render: (_, r) => `${r.customerName}${r.phone ? ` · ${r.phone}` : ''}` },
    { title: 'Dịch vụ', width: 200, dataIndex: 'itemName' },
    { title: 'SL', width: 50, align: 'right', dataIndex: 'qty' },
    { title: 'Đơn giá', width: 110, align: 'right', render: (_, r) => money(r.price) },
    { title: 'Giảm', width: 100, align: 'right', render: (_, r) => money(r.discount) },
    { title: 'Ghi chú kiểm tra', width: 320, render: (_, r) => <span className={r.level === 'error' ? 'text-red-600' : 'text-amber-700'}>{r.messages.join('; ')}</span> },
  ];

  return (
    <div className="space-y-5">
      <Steps current={step} items={[{ title: 'Tải file' }, { title: 'Ghép cột' }, { title: 'Kiểm tra' }, { title: 'Import' }]} />

      {step === 0 && (
        <div className="space-y-3">
          <Upload.Dragger accept=".xlsx,.xls,.csv" maxCount={1} showUploadList={false} beforeUpload={readFile}>
            <FileSpreadsheet className="mx-auto mb-2 h-10 w-10 text-[#3e91ab]" />
            <p className="text-base font-semibold">Kéo thả hoặc bấm để chọn file xuất từ MySpa</p>
            <p className="text-sm text-slate-500">.xlsx / .xls / .csv · tối đa 10MB, {MAX_ROWS.toLocaleString('vi-VN')} dòng</p>
          </Upload.Dragger>
          <button onClick={downloadTemplate} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Download className="h-4 w-4" /> Tải file mẫu</button>
        </div>
      )}

      {step === 1 && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">File <b>{fileName}</b> · {rows.length} dòng · {headers.length} cột. Hệ thống tự ghép theo tên cột; kiểm tra lại các trường bên dưới.</p>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-3 py-2 text-left">Trường hệ thống</th><th className="px-3 py-2 text-left">Cột trong file</th><th className="px-3 py-2 text-left">Giá trị dòng đầu</th></tr></thead>
              <tbody>
                {FIELDS.map(f => (
                  <tr key={f.key} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium">{f.label}{f.required && <span className="text-red-500"> *</span>}</td>
                    <td className="px-3 py-2">
                      <Select allowClear className="w-64" placeholder="— Không ghép —" status={f.required && !mapping[f.key] ? 'error' : undefined}
                        value={mapping[f.key]} onChange={v => setMapping(m => ({ ...m, [f.key]: v }))} options={headers.map(h => ({ value: h, label: h }))} />
                    </td>
                    <td className="px-3 py-2 text-slate-500">{mapping[f.key] ? String(rows[0]?.[mapping[f.key]!] instanceof Date ? fmtDateTime((rows[0][mapping[f.key]!] as Date).toISOString()) : rows[0]?.[mapping[f.key]!] ?? '') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {missingRequired.length > 0 && <p className="text-sm text-red-600">Chưa ghép trường bắt buộc: {missingRequired.map(f => f.label).join(', ')}</p>}
          <div className="flex gap-2">
            <button onClick={() => setStep(0)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold">Chọn file khác</button>
            <button disabled={missingRequired.length > 0} onClick={() => setStep(2)} className="h-10 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white disabled:opacity-50">Tiếp tục kiểm tra</button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {[
              ['Tổng dòng', stats.total, 'text-slate-800'], ['Số đơn sau gộp', stats.orders, 'text-slate-800'], ['Hợp lệ', stats.ok, 'text-green-700'],
              ['Cảnh báo', stats.warning, 'text-amber-600'], ['Lỗi (bị loại)', stats.error, 'text-red-600'],
            ].map(([l, v, c]) => (
              <div key={l as string} className="rounded-xl border border-slate-200 bg-white p-3"><div className="text-xs text-slate-500">{l}</div><div className={`text-2xl font-bold ${c}`}>{v as number}</div></div>
            ))}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Radio.Group value={levelFilter} onChange={e => setLevelFilter(e.target.value)} optionType="button"
              options={[{ value: 'all', label: 'Tất cả' }, { value: 'ok', label: 'Hợp lệ' }, { value: 'warning', label: 'Cảnh báo' }, { value: 'error', label: 'Lỗi' }]} />
            <div className="flex items-center gap-2 text-sm">
              Đơn đã tồn tại:
              <Radio.Group value={mode} onChange={e => setMode(e.target.value)} options={[{ value: 'skip', label: 'Bỏ qua' }, { value: 'overwrite', label: 'Ghi đè' }]} />
            </div>
          </div>
          <Table<CheckedRow> rowKey="index" size="small" columns={previewColumns} scroll={{ x: 'max-content', y: 420 }} pagination={{ pageSize: 50, hideOnSinglePage: true }}
            dataSource={checked.filter(r => levelFilter === 'all' || r.level === levelFilter)}
            rowClassName={r => (r.level === 'error' ? 'bg-red-50' : r.level === 'warning' ? 'bg-amber-50/60' : '')} />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setStep(1)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold">Quay lại ghép cột</button>
            {(stats.error + stats.warning) > 0 && <button onClick={downloadErrors} className="flex h-10 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold"><Download className="h-4 w-4" /> Tải danh sách lỗi</button>}
            <button disabled={!orderCodes.length || orderCodes.length === willSkip} onClick={() => { setStep(3); runImport(); }} className="h-10 rounded-lg bg-[#357f96] px-4 text-sm font-semibold text-white disabled:opacity-50">
              Import {orderCodes.length - willSkip} đơn hợp lệ
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <Progress percent={Math.min(progress, 100)} status={progress >= 100 ? 'success' : 'active'} strokeColor="#3e91ab" />
          {progress < 100 && <p className="text-sm text-amber-700">Đang import… vui lòng không đóng trang.</p>}
          {result && (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 text-lg font-bold text-green-700"><CheckCircle2 className="h-6 w-6" /> Hoàn tất lô {result.id}</div>
              <div className="mt-2 flex flex-wrap gap-4 text-sm">
                <span>Thành công: <b>{result.success}</b> đơn</span>
                <span>Bỏ qua (trùng): <b>{result.skipped}</b></span>
                <span>Dòng lỗi bị loại: <b className="text-red-600">{result.failed}</b></span>
              </div>
              <div className="mt-3 flex gap-2">
                {result.failed > 0 && <button onClick={downloadErrors} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold"><Download className="h-4 w-4" /> Tải file lỗi</button>}
                <button onClick={() => { setStep(0); setRows([]); setResult(null); onDone(); }} className="h-9 rounded-lg bg-[#357f96] px-3 text-sm font-semibold text-white">Xem lịch sử import</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function History() {
  const batches = usePosStore(s => s.importBatches);
  const orders = usePosStore(s => s.orders);
  const rollback = usePosStore(s => s.rollbackImport);
  const { can } = usePosAuth();
  const [modal, ctx] = Modal.useModal();

  const confirmRollback = (b: ImportBatch) => modal.confirm({
    title: `Hoàn tác lô ${b.id}?`, icon: <AlertTriangle className="mr-2 h-5 w-5 text-amber-500" />,
    content: `Xóa ${orders.filter(o => o.importBatchId === b.id).length} đơn của lô này khỏi danh sách đơn và thống kê. Khách đã tạo vẫn được giữ lại (BR9.5).`,
    okText: 'Tiếp tục', cancelText: 'Hủy',
    onOk: () => modal.confirm({
      title: 'Xác nhận lần 2', icon: <XCircle className="mr-2 h-5 w-5 text-red-500" />, content: 'Thao tác không thể khôi phục. Hoàn tác lô import?',
      okText: 'Hoàn tác', okButtonProps: { danger: true }, cancelText: 'Hủy',
      onOk: () => { rollback(b.id); message.success(`Đã hoàn tác lô ${b.id}`); },
    }),
  });

  return (
    <>
      {ctx}
      <Table<ImportBatch> rowKey="id" size="middle" dataSource={batches} locale={{ emptyText: 'Chưa có lô import nào' }} pagination={{ pageSize: 20, hideOnSinglePage: true }}
        columns={[
          { title: 'Lô', dataIndex: 'id', width: 100 },
          { title: 'File', dataIndex: 'fileName' },
          { title: 'Thời gian', width: 150, render: (_, b) => fmtDateTime(b.at) },
          { title: 'Người import', dataIndex: 'by', width: 130 },
          { title: 'Dòng', dataIndex: 'totalRows', width: 80, align: 'right' },
          { title: 'Thành công', dataIndex: 'success', width: 100, align: 'right' },
          { title: 'Bỏ qua', dataIndex: 'skipped', width: 80, align: 'right' },
          { title: 'Lỗi', dataIndex: 'failed', width: 70, align: 'right' },
          { title: 'Chế độ', width: 90, render: (_, b) => (b.mode === 'skip' ? 'Bỏ qua' : 'Ghi đè') },
          { title: 'Trạng thái', width: 120, render: (_, b) => (b.status === 'done' ? <span className="text-green-700">Hoàn tất</span> : <span className="text-slate-400">Đã hoàn tác</span>) },
          {
            title: '', width: 120, render: (_, b) => b.status === 'done' && can('import') && (
              <button onClick={() => confirmRollback(b)} className="flex items-center gap-1 text-sm font-semibold text-red-600"><RotateCcw className="h-4 w-4" /> Hoàn tác</button>
            ),
          },
        ]} />
    </>
  );
}
